import React from "react";
import { useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next"; // Import useTranslation

import {
  useGetProductBySlugQuery,
  useGetRelatedProductsQuery,
  useGetProductVariationsQuery,
} from "../../api/productApi";
import {
  useProductVariationSelector,
  type UseProductVariationSelectorResult,
} from "../../hooks";
import ProductGallery from "../common/ProductGallery";
import ProductDetails from "./ProductDetails/ProductDetails";
import ProductPanels from "./ProductPanels"; // Renamed import
import RelatedProducts from "./RelatedProducts";
import ProductBreadcrumbs from "./ProductBreadcrumbs";
import RTKQErrorCard from "../../../../common/components/RTKQErrorCard";

import type {
  TransformedProduct,
  TransformedVariation,
} from "../../types/productApiTypes";
import type { RTKQueryError } from "../../../../common/components/RTKQErrorCard";

const SingleProductContainer: React.FC = () => {
  const { productSlug } = useParams<{ productSlug: string }>();
  const { ready: translationsReady } = useTranslation(); // Get translation ready state

  // --- Fetch Core Product Data (includes enriched categories/tags/attributes/variations) ---
  const {
    data: product,
    isLoading: productIsLoading,
    isError: productIsError,
    error: productErrorData,
  } = useGetProductBySlugQuery(productSlug!, {
    skip: !productSlug,
  });

  // --- Fetch Full Variation Data (needed for price, stock, gallery, description etc.) ---
  // We skip if it's not a variable product OR if the parent product data indicates no variations exist
  // (using enrichedVariations length as the indicator, assuming it's reliable from the API extension)
  const skipVariationsFetch =
    !product?.id ||
    product.type !== "variable" ||
    !product.enrichedVariations ||
    product.enrichedVariations.length === 0;

  const {
    data: variations = [] as TransformedVariation[],
    isLoading: variationsLoading, // Track variation fetching state
    isError: variationsError,
    error: variationsErrorData,
  } = useGetProductVariationsQuery(product?.id!, {
    skip: skipVariationsFetch,
  });

  // --- Variation Selection Logic ---
  // The hook handles URL sync and finding the matched variation ID/object
  const {
    selectedAttributeSlugs, // Map of { 'pa_color': 'blue', ... } for UI/URL
    selectedVariation, // The matched TransformedVariation object (or null)
    handleAttributeSelect,
    variationSelectionComplete, // Flag: true when URL sync AND initial selection are done
  }: UseProductVariationSelectorResult = useProductVariationSelector(
    product,
    variations, // Pass full variations data for the hook to find the match
    true // Enable URL sync
  );

  // --- Combined Loading State ---
  // Wait for product data, variation selection logic (including URL sync), AND translations to be ready
  const isLoading =
    productIsLoading || !variationSelectionComplete || !translationsReady;

  // --- Fetch Related Products ---
  const {
    data: relatedProducts = [] as TransformedProduct[],
    isLoading: relatedProductsLoading,
  } = useGetRelatedProductsQuery(
    // Filter out the current product ID from its own related IDs
    product?.relatedProductIds?.filter((id) => id !== product?.id) || [],
    {
      // Skip if no product, no related IDs, or related IDs array is empty
      skip:
        !product?.id ||
        !product?.relatedProductIds ||
        product.relatedProductIds.length === 0,
    }
  );

  // --- Combined Loading State for Related Products (includes translation readiness) ---
  const isRelatedLoading = relatedProductsLoading || !translationsReady;

  // --- Render Logic ---

  // 1. Loading State (Skeleton)
  if (isLoading) {
    return (
      <div className="single-product-container">
        <div className="product-main">
          <div className="product-breadcrumbs">
            <Skeleton width={250} height={20} />
          </div>
          <div className="product-content">
            <div className="product-gallery-container">
              <Skeleton height={400} className="gallery-skeleton" />
              <div className="thumbnails-skeleton">
                <Skeleton
                  height={80}
                  width={80}
                  count={4}
                  inline={true}
                  style={{ marginRight: "10px" }}
                />
              </div>
            </div>
            <div className="product-details-container">
              <Skeleton height={36} width="80%" className="title-skeleton" />
              <Skeleton height={28} width={150} className="price-skeleton" />
              <Skeleton count={3} className="description-skeleton" />
              <div className="cart-form-skeleton">
                {/* Skeleton for variation selectors (example) */}
                <Skeleton
                  height={30}
                  width="40%"
                  style={{ marginBottom: "8px" }}
                />
                <Skeleton height={42} style={{ marginBottom: "16px" }} />
                <Skeleton
                  height={30}
                  width="40%"
                  style={{ marginBottom: "8px" }}
                />
                <Skeleton height={42} style={{ marginBottom: "16px" }} />
                {/* Quantity and Button Skeletons */}
                <Skeleton
                  height={45}
                  width={160}
                  className="quantity-skeleton"
                  style={{ marginBottom: "1rem" }}
                />
                <Skeleton height={50} className="button-skeleton" />
              </div>
              <div className="meta-skeleton">
                <Skeleton count={3} width="60%" />
              </div>
            </div>
          </div>
          <div className="product-sections-container">
            <ProductPanels.Skeleton /> {/* Use renamed skeleton */}
          </div>
          <div className="related-products-container">
            <RelatedProducts products={[]} isLoading={true} />
          </div>
        </div>
      </div>
    );
  }

  // 2. Product Fetch Error State
  if (productIsError) {
    return (
      <div className="single-product-container single-product-container-error">
        <RTKQErrorCard
          error={productErrorData as RTKQueryError | null}
          title="Error loading product"
          maxHeight="300px"
        />
        {/* Optionally add a link back to shop */}
        <a
          href="/shop"
          className="button"
          style={{ marginTop: "1rem", display: "inline-block" }}
        >
          Return to Shop
        </a>
      </div>
    );
  }

  // 3. Product Not Found State
  if (!product) {
    return (
      <div className="single-product-container single-product-container-not-found">
        <h1>Product Not Found</h1>
        <p>The product you're looking for doesn't exist or has been removed.</p>
        <a href="/shop" className="button">
          Return to Shop
        </a>
      </div>
    );
  }

  // Optional: Handle variation fetching error separately if needed
  // You might want to display a message or disable variation selection controls
  if (product.type === "variable" && variationsError) {
    console.error("Error fetching variations:", variationsErrorData);
    // Example: Display an error within the details section (can be passed as a prop)
    // Or maybe disable the attribute selectors in ProductDetails
  }

  // 4. Success State - Render the full product view
  return (
    <div className="single-product-container">
      <div className="product-main">
        {/* ProductBreadcrumbs now only needs the product object */}
        <ProductBreadcrumbs product={product} />

        <div className="product-content">
          <div className="product-gallery-container">
            {/* ProductGallery needs product, selectedVariation, and loading status */}
            <ProductGallery
              product={product} // Pass the full product object
              selectedVariation={selectedVariation}
              variationsLoading={variationsLoading}
            />
          </div>
          <div className="product-details-container">
            {/* ProductDetails needs product, variation info, selection state, and the select handler */}
            <ProductDetails
              product={product} // Pass the full product object
              // variations={variations} // Keep passing variations if the hook needs them to retrieve the full object after matching ID
              variationsLoading={variationsLoading}
              selectedVariation={selectedVariation}
              selectedAttributes={selectedAttributeSlugs} // Pass the slug map for UI
              onAttributeSelect={handleAttributeSelect}
              // Categories and Tags are now derived internally by ProductDetails from the 'product' prop
            />
          </div>
        </div>

        <div className="product-sections-container">
          {/* ProductPanels needs product, loading status, and selected variation */}
          <ProductPanels // Use renamed component
            product={product} // Pass the full product object
            selectedVariation={selectedVariation} // For potential description override
            isLoading={isLoading} // Pass the combined loading state
          />
        </div>

        <div className="related-products-container">
          {/* RelatedProducts */}
          <RelatedProducts
            products={relatedProducts}
            isLoading={isRelatedLoading} // Pass the combined loading state
          />
        </div>
      </div>
    </div>
  );
};

export default SingleProductContainer;
